import { useState } from "react";
import { useNavigate, Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Edit2, FileText, Calendar } from "lucide-react";
import { getPersonaByValue } from "../utils/personas";
import { trackEvent } from "../utils/mixpanel";

function ReportConfiguration({ report }) {
  const navigate = useNavigate();
  const [showPlanModal, setShowPlanModal] = useState(false);

  const stripMarkdown = (text) => {
    if (!text) return "";
    return text
      .replace(/#{1,6}\s+/g, "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1")
      .replace(/__([^_]+)__/g, "$1")
      .replace(/_([^_]+)_/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/>\s+/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const displayTitle = stripMarkdown(report?.request || "");
  const planCount = report?.plan?.length || 0;

  // Format date helper
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month} ${day} '${year}`;
  };

  return (
    <>
      <Card className="mb-6 group relative border-transparent bg-transparent hover:border-border hover:bg-card transition-all duration-200">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold leading-tight line-clamp-3 mb-3">{displayTitle}</h1>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-3 text-xs border-border/50 hover:border-border"
                  disabled
                >
                  {getPersonaByValue(report?.persona).label}
                </Button>
                {planCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 text-xs border-border/50 hover:border-border"
                    onClick={() => {
                      trackEvent("Plan Viewed", {
                        report_id: report.id,
                        plan_count: planCount,
                        persona: report?.persona,
                      });
                      setShowPlanModal(true);
                    }}
                  >
                    <FileText className="h-3.5 w-3.5 mr-1.5" />
                    Plan ({planCount})
                  </Button>
                )}
                {report?.timestamp && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-3 text-xs border-border/50 hover:border-border"
                          disabled
                        >
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          {formatDate(report.timestamp)}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{new Date(report.timestamp).toLocaleString()}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>

          <Button
            onClick={() => navigate(`/v1/report/${report.id}/edit`)}
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </CardContent>
      </Card>

      {/* Plan Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Plan</DialogTitle>
            <DialogDescription>
              The following steps will be executed to generate your report:
            </DialogDescription>
          </DialogHeader>

          <div className="my-4">
            {report?.plan && report.plan.length > 0 ? (
              <ol className="list-decimal list-inside space-y-3">
                {report.plan.map((step, index) => (
                  <li key={index} className="text-sm leading-relaxed">
                    {step.sub_request || step.title || `Step ${index + 1}`}
                    {step.slides && step.slides.length > 0 && (
                      <ul className="list-disc list-inside mt-1 ml-5 space-y-1">
                        {step.slides.map((slide, slideIndex) => (
                          <li key={slideIndex} className="text-sm text-muted-foreground">
                            {slide.sub_request || `Slide ${slideIndex + 1}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted-foreground">No plan steps available.</p>
            )}
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="outline" asChild>
              <Link to={`/v1/report/${report.id}/edit`}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Plan
              </Link>
            </Button>
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReportConfiguration;
